import { BEARER_PREFIX } from '@/lib/api/constants';
import { isKeycloakAuth } from '@/lib/auth/authConfig';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

async function getAuthToken(): Promise<string | null> {
  if (isKeycloakAuth) {
    try {
      const localStorageToken = typeof window !== 'undefined'
        ? localStorage.getItem('keycloak_access_token')
        : null;

      const headers: HeadersInit = {};
      if (localStorageToken) {
        // Send token  Authorization header for implicit flow
        headers['Authorization'] = `${BEARER_PREFIX}${localStorageToken}`;
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.access_token ?? null;
    } catch {
      return null;
    }
  } else {
    const { auth } = await import('@/firebase/config');
    const currentUser = auth().currentUser;
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }
}

export type QueryParams = Record<string, string | number | boolean | undefined>;

class ApiClient {
  private baseUrl = '/api';

  private async request<T>(
    method: string,
    path: string,
    options?: {
      params?: QueryParams;
      body?: unknown;
      isRetry?: boolean;
    }
  ): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);

    if (options?.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.set(key, String(value));
        }
      });
    }

    const token = await getAuthToken();
    if (!token) {
      throw new ApiError(401, 'Not authenticated');
    }

    const headers: HeadersInit = {
      'Authorization': `${BEARER_PREFIX}${token}`
    };

    if (options?.body) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(url.toString(), {
      method,
      headers,
      credentials: 'include',
      body: options?.body ? JSON.stringify(options.body) : undefined
    });

    if (!res.ok) {
      const data = await res.json().catch(() => null);

      if (res.status === 401 && !options?.isRetry) {
        const newToken = await getAuthToken();
        if (newToken) {
          return this.request<T>(method, path, { ...options, isRetry: true });
        }
      }

      throw new ApiError(res.status, res.statusText, data);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return res.json();
  }

  get<T>(path: string, params?: QueryParams): Promise<T> {
    return this.request<T>('GET', path, { params });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, { body });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, { body });
  }

  delete<T>(path: string, params?: QueryParams): Promise<T> {
    return this.request<T>('DELETE', path, { params });
  }
}

export const apiClient = new ApiClient();
